import { useState, useRef, useEffect } from 'react';
import { Camera, X, RefreshCw, Upload, Minimize2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

interface CameraCaptureProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageData: string) => void;
}

export const CameraCapture = ({ open, onClose, onCapture }: CameraCaptureProps) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [hasCamera, setHasCamera] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // Detect mobile device
    const checkMobile = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (open && hasCamera) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [open, facingMode]);

  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false,
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Camera access error:', error);
      setHasCamera(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg', 0.8);
        setCapturedImage(imageData);
      }
    }
  };

  const handleConfirm = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      handleClose();
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
  };

  const handleClose = () => {
    setCapturedImage(null);
    stopCamera();
    onClose();
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageData = event.target?.result as string;
        onCapture(imageData);
        handleClose();
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={isMobile ? "max-w-full w-screen h-screen p-0 m-0 rounded-none border-0" : "max-w-2xl"}>
        {!hasCamera ? (
          <div className="space-y-4 text-center py-8 px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold">Camera Not Available</h3>
            <p className="text-muted-foreground">Please upload a photo from your gallery instead.</p>
            <label htmlFor="file-upload">
              <Button asChild size="lg" className="w-full">
                <span>
                  <Upload className="mr-2 h-5 w-5" />
                  Choose Photo from Gallery
                </span>
              </Button>
            </label>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        ) : (
          <div className={isMobile ? "h-full flex flex-col bg-black" : "space-y-4"}>
            {/* Mobile Header */}
            {isMobile && (
              <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-white font-semibold text-lg">
                    {capturedImage ? 'Review Photo' : 'Capture Box Contents'}
                  </h2>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleClose}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="h-6 w-6" />
                  </Button>
                </div>
              </div>
            )}

            {/* Desktop Header */}
            {!isMobile && (
              <div className="flex items-center justify-between pb-2">
                <h2 className="text-lg font-semibold">
                  {capturedImage ? 'Review Photo' : 'Capture Box Contents'}
                </h2>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleClose}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            )}
            
            {/* Camera/Preview Area */}
            <div className={isMobile 
              ? "relative flex-1 bg-black" 
              : "relative w-full h-[60vh] sm:h-[500px] bg-black rounded-lg overflow-hidden"
            }>
              {capturedImage ? (
                <img 
                  src={capturedImage} 
                  alt="Captured" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {/* Camera guide overlay for mobile */}
                  {isMobile && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-x-4 top-24 bottom-32 border-2 border-white/30 rounded-lg">
                        <div className="absolute -top-8 left-0 right-0 text-center">
                          <p className="text-white text-sm font-medium bg-black/50 inline-block px-4 py-1 rounded-full">
                            Position box contents in frame
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            {/* Controls */}
            <div className={isMobile 
              ? "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent pb-8 pt-12 px-4"
              : "flex gap-2 justify-center"
            }>
              {capturedImage ? (
                <div className={isMobile 
                  ? "flex gap-3 justify-center"
                  : "flex gap-2 justify-center"
                }>
                  <Button 
                    variant="outline" 
                    onClick={handleRetake} 
                    size={isMobile ? "lg" : "default"}
                    className={isMobile ? "flex-1 bg-white/90 hover:bg-white text-black font-semibold" : ""}
                  >
                    <RefreshCw className="mr-2 h-5 w-5" />
                    Retake
                  </Button>
                  <Button 
                    onClick={handleConfirm} 
                    size={isMobile ? "lg" : "default"}
                    className={isMobile ? "flex-1 bg-primary hover:bg-primary/90 font-semibold" : ""}
                  >
                    <Camera className="mr-2 h-5 w-5" />
                    Use Photo
                  </Button>
                </div>
              ) : (
                <div className={isMobile 
                  ? "flex flex-col gap-4 items-center"
                  : "flex gap-2 justify-center"
                }>
                  {/* Main capture button - large circular on mobile */}
                  {isMobile ? (
                    <>
                      <button
                        onClick={capturePhoto}
                        className="w-20 h-20 rounded-full bg-white border-4 border-primary shadow-lg active:scale-95 transition-transform flex items-center justify-center"
                        aria-label="Capture photo"
                      >
                        <div className="w-16 h-16 rounded-full bg-primary" />
                      </button>
                      <div className="flex gap-4">
                        <Button 
                          variant="ghost" 
                          onClick={switchCamera}
                          className="text-white hover:bg-white/20"
                        >
                          <RefreshCw className="mr-2 h-5 w-5" />
                          Switch Camera
                        </Button>
                        <label htmlFor="mobile-upload">
                          <Button 
                            variant="ghost"
                            className="text-white hover:bg-white/20"
                            asChild
                          >
                            <span>
                              <ImageIcon className="mr-2 h-5 w-5" />
                              Gallery
                            </span>
                          </Button>
                        </label>
                        <input
                          id="mobile-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" onClick={switchCamera}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Switch Camera
                      </Button>
                      <Button onClick={capturePhoto} size="lg">
                        <Camera className="mr-2 h-5 w-5" />
                        Capture
                      </Button>
                      <label htmlFor="desktop-upload">
                        <Button variant="outline" asChild>
                          <span>
                            <Upload className="mr-2 h-4 w-4" />
                            Upload
                          </span>
                        </Button>
                      </label>
                      <input
                        id="desktop-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
